import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays';

import { disconnectSalesforce } from '../api/install';

interface DisconnectConfirmationProps {
  orgId: number | string;
  isOpen: boolean;
  onClose: () => void;
  onDisconnected?: () => void;
}

const OUTCOME_CLASS =
  'flex gap-2 text-body-small text-[var(--color-text-secondary)]';

export function DisconnectConfirmation({
  orgId,
  isOpen,
  onClose,
  onDisconnected,
}: DisconnectConfirmationProps) {
  const queryClient = useQueryClient();

  const { mutate, isPending, isError } = useMutation({
    mutationFn: () => disconnectSalesforce(orgId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['salesforceStatus', orgId],
      });
      onDisconnected?.();
      onClose();
    },
  });

  const handleConfirm = useCallback(() => {
    mutate();
  }, [mutate]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      variant='destructive'
      title='Disconnect Salesforce?'
      description='Importing will stop right away. Nothing is deleted.'
      confirmLabel='Disconnect'
      isLoading={isPending}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      instance='disconnect-salesforce'
    >
      <ul className='mt-3 space-y-2'>
        <li className={OUTCOME_CLASS}>
          <span aria-hidden='true'>•</span>
          <span>
            Accounts, contacts, and opportunities already imported stay in Ahoy.
          </span>
        </li>
        <li className={OUTCOME_CLASS}>
          <span aria-hidden='true'>•</span>
          <span>Your field mappings are saved, so reconnecting is quick.</span>
        </li>
      </ul>
      {isError && (
        <p className='mt-3 text-body-small text-[var(--color-text-error-primary)]'>
          Couldn't disconnect. Please try again.
        </p>
      )}
    </ConfirmationDialog>
  );
}
